import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { 
  Building2, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  CheckCircle,
  Package,
  Truck,
  DollarSign,
  TrendingUp,
  Activity,
  ArrowLeft,
  Users,
  ShoppingCart,
  Clock,
  BarChart3,
  Contact
} from "lucide-react";
import { getPharmacyById, Pharmacy } from "../../api/pharmacyService";
import { toast } from "sonner";
import axiosInstance from "../../api/axiosInstance";
import GlobalLoader from "../../components/ui/GlobalLoader";

interface PharmacyOrder {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  status: string;
  totalPrice: number;
  deliveryDate: string;
  createdAt: string;
  medicines: any[];
}

interface PharmacyAnalytics {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersThisWeek: number;
  ordersThisMonth: number;
  revenueThisMonth: number;
}

export function PharmacyDetailsPage() {
  const { pharmacyId } = useParams<{ pharmacyId: string }>();
  const navigate = useNavigate();
  const [pharmacy, setPharmacy] = useState<Pharmacy | null>(null);
  const [orders, setOrders] = useState<PharmacyOrder[]>([]);
  const [analytics, setAnalytics] = useState<PharmacyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Fetch pharmacy details
  const fetchPharmacyDetails = async () => {
    if (!pharmacyId) return;
    
    try {
      setLoading(true);
      const pharmacyData = await getPharmacyById(pharmacyId);
      
      if (pharmacyData) {
        setPharmacy(pharmacyData);
      } else {
        toast.error('Pharmacy not found');
        navigate('/admin/pharmacies');
      }
    } catch (error) {
      console.error('Error fetching pharmacy:', error);
      toast.error('Failed to load pharmacy details');
      navigate('/admin/pharmacies');
    } finally {
      setLoading(false);
    }
  };

  // Fetch pharmacy orders
  const fetchPharmacyOrders = async () => {
    if (!pharmacyId) return;
    
    try {
      setOrdersLoading(true);
      const response = await axiosInstance.get(`/admin/pharmacies/${pharmacyId}/orders`);
      
      if (response.data?.data?.orders) {
        setOrders(response.data.data.orders);
      } else if (response.data?.orders) {
        setOrders(response.data.orders);
      } else if (Array.isArray(response.data)) {
        setOrders(response.data);
      }
    } catch (error) {
      console.error('Error fetching pharmacy orders:', error);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Fetch pharmacy analytics
  const fetchPharmacyAnalytics = async () => {
    if (!pharmacyId) return;
    
    try {
      const response = await axiosInstance.get(`/admin/pharmacies/${pharmacyId}/analytics`);
      
      if (response.data?.data) {
        setAnalytics(response.data.data);
      } else if (response.data) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching pharmacy analytics:', error);
    }
  };

  useEffect(() => {
    fetchPharmacyDetails();
    fetchPharmacyOrders();
    fetchPharmacyAnalytics();
  }, [pharmacyId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pickup-requested': return 'bg-blue-100 text-blue-800';   
      case 'assigned': return 'bg-orange-100 text-orange-800';
      case 'in-transit': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Processing please wait..." 
        subMessage="Please wait" 
      />
    );
  }

  if (!pharmacy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">Pharmacy not found</p>
          <Button onClick={() => navigate('/admin/pharmacies')} className="mt-4">
            Back to Pharmacies
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/pharmacies')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">{pharmacy.pharmacyName || pharmacy.name}</h1>
            <p className="text-gray-600 mt-1">Pharmacy analytics and performance metrics</p>
          </div>
        </div>
        <Badge 
          className={
            pharmacy.accountStatus === 'verified' || pharmacy.isVerified
              ? "bg-green-100 text-green-800"
              : pharmacy.accountStatus === 'pending'
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          {pharmacy.accountStatus === 'verified' || pharmacy.isVerified ? 'Verified' : pharmacy.accountStatus}
        </Badge>
      </div>

      {/* Contact Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Pharmacy Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Contact Person</p>
              <p className="font-medium">{pharmacy.firstName} {pharmacy.lastName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Email</p>
              <p className="font-medium flex items-center">
                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                {pharmacy.email}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Phone</p>
              <p className="font-medium flex items-center">
                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                {pharmacy.phone}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Joined Date</p>
              <p className="font-medium flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                {new Date(pharmacy.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          {/* <div className="mt-4">
            <p className="text-sm text-gray-500 mb-1">Address</p>
            <p className="font-medium flex items-start">
              <MapPin className="w-4 h-4 mr-2 mt-0.5 text-gray-400" />
              <span>
                {pharmacy.address?.streetAddress}, {pharmacy.address?.city}, {pharmacy.address?.state} {pharmacy.address?.zipCode}
              </span>
            </p>
          </div> */}
          <div className="flex mt-4 gap-2">
            <p className="text-md font-bold">Pharmacy Address:</p>
            <div className="flex items-center space-x-1 my-1 text-sm text-gray-500">
              <MapPin className="w-4 h-4 mr-1" />
              {
                pharmacy.location === 'string' 
                ? pharmacy.location 
                : pharmacy.location?.address || ''
              }
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-3xl font-bold text-gray-900">{analytics?.totalOrders || pharmacy.totalOrders || 0}</p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <Package className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold">{analytics?.ordersThisMonth || 0}</p>
                <p className="text-xs text-gray-500">orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">This Week</p>
                <p className="text-2xl font-bold">{analytics?.ordersThisWeek || 0}</p>
                <p className="text-xs text-gray-500">orders</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Orders</p>
                <p className="text-3xl font-bold text-orange-600">{pharmacy.activeOrders || 0}</p>
                <p className="text-xs text-gray-500 mt-1">In progress</p>
              </div>
              <Truck className="w-10 h-10 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-green-600">{analytics?.completedOrders || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Successfully delivered</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>    

        

        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold text-purple-600">${analytics?.totalRevenue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <DollarSign className="w-10 h-10 text-purple-600" />
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        

        {/* <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Avg Order Values</p>
                <p className="text-2xl font-bold">${analytics?.averageOrderValue?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-gray-500">per order</p>
              </div>
            </div>
          </CardContent>
        </Card> */}
      </div>

      {/* Orders Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <ShoppingCart className="w-5 h-5 mr-2" />
              Recent Orders
            </div>
            <Badge variant="outline">{orders.length} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ordersLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
              <p className="text-gray-500">This pharmacy hasn't placed any orders yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.slice(0, 10).map((order) => (
                <div key={order._id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{order.orderNumber}</p>
                      <p className="text-sm text-gray-600">{order.customerName}</p>
                    </div>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Customer Phone:</span>
                      <p className="font-medium">{order.customerPhone}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Delivery Date:</span>
                      <p className="font-medium">
                        {order.deliveryDate || 'N/A'}
                        {(order as any).deliveryTime && ` at ${(order as any).deliveryTime}`}
                      </p>
                    </div>
                    {/* <div>
                      <span className="text-gray-500">Total:</span>
                      <p className="font-medium text-green-600">${order.totalPrice?.toFixed(2) || '0.00'}</p>
                    </div> */}
                  </div>

                  {order.medicines && order.medicines.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-2">Medicines:</p>
                      <div className="flex flex-wrap gap-2">
                        {order.medicines.slice(0, 3).map((medicine: any, index: number) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {medicine.name}
                          </Badge>
                        ))}
                        {order.medicines.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{order.medicines.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {orders.length > 10 && (
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-500">Showing 10 of {orders.length} orders</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Monthly Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Orders This Month:</span>
                <span className="text-2xl font-bold text-blue-600">{analytics?.ordersThisMonth || 0}</span>
              </div>
              {/* <div className="flex justify-between items-center">
                <span className="text-gray-600">Revenue This Month:</span>
                <span className="text-2xl font-bold text-green-600">${analytics?.revenueThisMonth?.toFixed(2) || '0.00'}</span>
              </div> */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending Orders:</span>
                <span className="text-2xl font-bold text-yellow-600">{analytics?.pendingOrders || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              Account Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Account Type:</span>
                <Badge variant="outline">{pharmacy.accountType || 'Standard'}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Account Status:</span>
                <Badge 
                  className={
                    pharmacy.isVerified 
                      ? "bg-green-100 text-green-800" 
                      : "bg-red-100 text-red-800"
                  }
                >
                  {pharmacy.isVerified ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">{formatDate(pharmacy.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

